import { useEffect, useState, useCallback, Dispatch } from 'react'

const identity = <T>(x: T): T => x

export type ParserOptions<T> =
  | {
      raw: true
    }
  | {
      raw: false
      serializer: (value: T) => string
      deserializer: (value: string) => T
    }

export const useStorage = <T>(
  /**
   * Internal variable wheter we are doing it for local or session storage.
   */
  storage: Storage,

  /**
   * Be aware what strategy you choose for our key naming. Also understand our default storage format is JSON.
   * It depends wheter you have a single hostname or not how your key should look like.
   * Apps deployed on an own url are free to choose a name.
   * Shared apps should not use generic names, only when intentional and agreed between these.
   * So a key like `i18n` on shared url can lead to problems in other apps for example :)
   */
  key: string,

  /**
   * The default value when none given. Understand our default storage is JSON. So when you want to store 'light',
   * this will actually stored as '"light"'. When you want to have it as string then chooce for the next param the
   * option: { raw: true }
   */
  initialValue?: T,

  /**
   * When you do not want the JSON formatter. Be aware when once stored as JSON you need to handle this in your deserializer as well!
   * When you need just a string use { raw: true }
   * When you need something complex add the serializer and deserializer, these are also mandetory when you change
   * a format while having a storage allready. So for example you started as string, then want to go to JSON.
   */
  options?: ParserOptions<T>
): [T, Dispatch<T>] => {
  // eslint-disable-next-line no-nested-ternary
  const serializer = options ? (options.raw ? identity : options.serializer) : JSON.stringify

  // eslint-disable-next-line no-nested-ternary
  const deserializer = options ? (options.raw ? identity : options.deserializer) : JSON.parse

  const [state, setState] = useState<T>(() => {
    try {
      const storageValue = storage.getItem(key)

      if (storageValue !== null && storageValue !== 'undefined') {
        return deserializer(storageValue)
      }

      if (initialValue) {
        storage.setItem(key, serializer(initialValue))
      }

      return initialValue
    } catch {
      // Incognito mode
      return initialValue
    }
  })

  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      // check if we are in the correct Area (local/session), and for the particular key!
      if (e.storageArea === storage && e.key === key) {
        setState(e.newValue === null ? initialValue : deserializer(e.newValue))
      }
    }

    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('storage', onStorage)
    }
  }, [storage, deserializer, initialValue, key])

  const decoratedSetState = useCallback(
    (newState: T) => {
      setState(newState)

      try {
        storage.setItem(key, serializer(newState))

        window.dispatchEvent(
          new StorageEvent('storage', {
            key,
            newValue: serializer(newState),
            storageArea: storage,
          })
        )
      } catch {
        // Incognito mode
      }
    },
    [storage, setState, key, serializer]
  )

  return [state, decoratedSetState]
}
